import { FormInstance } from "antd";
import { SorterResult } from "antd/es/table/interface";
import {
  Error,
  Paging,
} from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseViewModel } from "app/based/baseViewModel/BaseViewModel";
import { SupplierManagementInjection } from "features/supplierManagement/di/SupplierManagmentInjection";
import { SupplierActionRequestEntity } from "features/supplierManagement/domain/entities/action/SupplierActionRequestEntity";
import { SupplierDetailResponseEntity } from "features/supplierManagement/domain/entities/detail/SupplierDetailResponseEntity";
import { SupplierResponseEntity } from "features/supplierManagement/domain/entities/SupplierResponseEntity";
import { SupplierRequestEntity } from "features/supplierManagement/domain/entities/SupplierResquestEntity";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { strings } from "utils/localizedStrings";

class SupplierManagementInput implements Disposable {
  public loading = new BehaviorSubject<boolean>(false);
  public fetchList = new BehaviorSubject<SupplierRequestEntity>({});
  public fetchDetail = new BehaviorSubject<SupplierRequestEntity>({});
  public fetchActions = new BehaviorSubject<{
    body?: SupplierActionRequestEntity;
    detailInfo?: SupplierDetailResponseEntity;
    action?: "create" | "update" | "delete";
  }>({});

  [Symbol.dispose](): void {
    this.loading.complete();
    this.fetchList.complete();
    this.fetchDetail.complete();
    this.fetchActions.complete();
  }
}

class SupplierManagementOutput implements Disposable {
  public list = new BehaviorSubject<Array<SupplierResponseEntity>>([]);
  public paging = new BehaviorSubject<Paging>({});
  public detailInfo = new BehaviorSubject<SupplierDetailResponseEntity>({});
  public onError = new BehaviorSubject<Error>({});
  public createUpdateSuccess = new BehaviorSubject<string>("");

  [Symbol.dispose](): void {
    this.list.complete();
    this.paging.complete();
    this.detailInfo.complete();
    this.onError.complete();
    this.createUpdateSuccess.complete();
  }
}

@injectable()
export class SupplierManagementViewModel extends BaseViewModel<
  SupplierManagementInput,
  SupplierManagementOutput
> {
  constructor() {
    super(new SupplierManagementInput(), new SupplierManagementOutput());
  }
  private supplierUseCase = SupplierManagementInjection.getUseCase();

  public buildTableQuery(
    pagination: any,
    sorter: SorterResult,
    filterForm: React.RefObject<FormInstance<any>>,
    prevParams: SupplierRequestEntity,
  ) {
    const formValues = filterForm?.current?.getFieldsValue(true) || {};
    const { created, ...rest } = formValues;

    const order =
      sorter?.order === "descend"
        ? `${String(sorter?.column?.dataIndex)} DESC`
        : sorter?.order === "ascend"
          ? `${String(sorter?.column?.dataIndex)} ASC`
          : `createdAt DESC`;

    const query = {
      ...prevParams,
      ...rest,
      page: pagination?.current - 1,
      limit: pagination?.pageSize,
      order,
    };

    const paginationState = {
      page: pagination?.current - 1,
      limit: pagination?.pageSize,
    };

    return {
      query,
      pagination: paginationState,
    };
  }

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchList, async (params) => {
      try {
        this.input.loading.next(true);
        const res = await this.supplierUseCase.getList(params);
        if (res?.data) {
          this.output.list.next(res?.data);
          this.output.paging.next(res?.pagination);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.input.loading.next(false);
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async (params) => {
      try {
        const res = await this.supplierUseCase.getDetail({ id: params?.id });
        if (res?.data) {
          this.output.detailInfo.next(res?.data);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      }
    });

    this.skipFirstInitial(this.input.fetchActions, async (props) => {
      let { action, body, detailInfo } = props;
      switch (action) {
        case "create":
          try {
            delete body?.id;
            const res = await this.supplierUseCase.createSupplier(
              body,
              detailInfo,
            );
            if (res?.data) {
              this.output.createUpdateSuccess.next(
                strings.GlobalMessage.CreateSuccessfully,
              );
            }
          } catch (error) {
            console.error(error);
            this.output.onError.next(error);
          }
          break;
        case "update":
          try {
            const res = await this.supplierUseCase.updateSupplier(
              body,
              detailInfo,
            );
            if (res?.data) {
              this.output.createUpdateSuccess.next(
                strings.GlobalMessage.UpdateSuccessfully,
              );
            }
          } catch (error) {
            console.error(error);
            this.output.onError.next(error);
          }
          break;
        case "delete":
          try {
            this.input.loading.next(true);
            const res = await this.supplierUseCase.deleteSupplier({
              id: body?.id,
            });
            if (res?.data) {
              this.output.createUpdateSuccess.next(
                strings.GlobalMessage.DeleteSuccessfully,
              );
            }
          } catch (error) {
            console.error(error);
            this.output.onError.next(error);
          } finally {
            this.input.loading.next(false);
          }
          break;
      }
    });
  }
}
